import { keyGenerator } from '@/utils/keyGenerator';
import { Pressable, SxProp, View } from 'dripsy';
import React from 'react';
import { isNull } from 'util';
import { TextBody } from '../typography';
import { TableViewColumn } from './TableView';
import { TableViewCell } from './TableViewCell';

interface TableViewRowProps<T> {
  item: T;
  index: number;
  columns: TableViewColumn<T>;
  rowMinWidth: number;
  gap?: number;
  rowVerticalAlign?: 'flex-start' | 'flex-end' | 'center';
  onRowPressed?: (item: T) => void;
  onRowUpdated?: (item: T) => SxProp;
}

const NO_PRESS_CURSOR = 'inherit';

export const TableViewRow = <T extends unknown>({
  item,
  index,
  columns,
  rowMinWidth,
  gap,
  rowVerticalAlign,
  onRowPressed,
  onRowUpdated
}: TableViewRowProps<T>) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <Pressable
      key={keyGenerator('table-row')}
      onPress={() => onRowPressed && onRowPressed(item)}
      sx={{ minWidth: rowMinWidth, cursor: !onRowPressed ? NO_PRESS_CURSOR : undefined, ...(onRowUpdated && onRowUpdated(item)) }}>
      <View>
        {index > 0 ? <View sx={{ ...styles.divider }} /> : null}
        <View
          sx={{
            ...styles.tableRow,
            backgroundColor: hovered ? '$disable' : 'transparent',
            alignItems: rowVerticalAlign || 'flex-start',
            gap: gap || '$5'
          }}>
          {columns.map((column) => (
            <TableViewCell
              key={keyGenerator('')}
              align={column.align}
              sx={{
                paddingY: '$2',
                width: column.width,
                minWidth: column.width || column.minWidth || 150,
                ...column.sx
              }}
              width={column.width}
              flex={column.flex}>
              {column.render ? (
                column.render(item)
              ) : column.formatValue ? (
                <TextBody>{column.formatValue(item)}</TextBody>
              ) : (
                <TextBody>{item[column.index]}</TextBody>
              )}
            </TableViewCell>
          ))}
        </View>
      </View>
    </Pressable>
  );
};

enum StyleName {
  container = 'container',
  tableRow = 'tableRow',
  divider = 'divider'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    flex: 1
  },
  divider: {
    height: 1,
    backgroundColor: '$borderButton'
  },
  tableRow: {
    padding: '$4',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '$5'
  }
};
